import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import dailyDispatchService, { DailyDispatch } from '../../services/api/dailyDispatchService';
import inspectionsService, { InspectionRow } from '../../services/api/inspectionsService';
import permitsService, { PermitRow } from '../../services/api/permitsService';
import taskMaterialsService, { TaskMaterial } from '../../services/api/taskMaterialsService';
import teamService, { TeamBonusPointsSummary, TeamOverviewRow } from '../../services/api/teamService';
import { Task } from '../../services/api/tasksService';
import accountsTransactionService from '../../services/api/accountsTransactionService';
import type { AccountsTransaction } from '../../services/api/roleWorkspaceService';
import inventoryMovementService, { InventoryMovementItem } from '../../services/api/inventoryMovementService';
import procurementService, { PurchaseOrder } from '../../services/api/procurementService';
import resourceService, { InventorySummaryRow } from '../../services/api/resourceService';
import { loadAllProjectTasks } from './supervisorFieldData';
import { normalizeStatus } from './workerFieldModeUtils';

/** Statuses past the point of field execution — excluded from look-ahead and dispatch queues. */
export const CLOSED_TASK_STATUSES = new Set([
  'INSPECTION_PASSED',
  'WORK_COMPLETE',
  'APPROVED',
  'PAYMENT_PENDING',
  'PAID',
  'CLOSED',
  'CANCELLED',
]);

export const taskStatusOf = (task: Task) => normalizeStatus(task.lifecycleStatus ?? task.status);

export const isOpenTask = (task: Task) => !CLOSED_TASK_STATUSES.has(taskStatusOf(task));

/** Look-ahead resource lines are fetched per task, so cap how many tasks we expand. */
const RESOURCE_TASK_CAP = 30;

const helperTaskCode = /^(PROC|STOR|ACCO)-/i;

export const isConstructionExecutionTask = (task: Task) =>
  (task.taskType ?? '').toLowerCase() === 'construction' && !helperTaskCode.test(task.taskCode ?? '');

interface PmFieldData {
  projectId: string | null;
  projectName: string;
  tasks: Task[];
  /** Task-linked resource allocations for the nearest upcoming open tasks (capped). */
  taskResources: Record<string, TaskMaterial[]>;
  teamOverview: TeamOverviewRow[];
  permits: PermitRow[];
  inspections: InspectionRow[];
  bonusPoints: TeamBonusPointsSummary | null;
  accountsTransactions: AccountsTransaction[];
  purchaseOrders: PurchaseOrder[];
  inventoryMovements: InventoryMovementItem[];
  inventorySummary: InventorySummaryRow[];
  dispatches: DailyDispatch[];
  loading: boolean;
  error: string | null;
  reload: () => void;
}

const emptyRows = {
  tasks: [] as Task[],
  taskResources: {} as Record<string, TaskMaterial[]>,
  teamOverview: [] as TeamOverviewRow[],
  permits: [] as PermitRow[],
  inspections: [] as InspectionRow[],
  bonusPoints: null as TeamBonusPointsSummary | null,
  accountsTransactions: [] as AccountsTransaction[],
  purchaseOrders: [] as PurchaseOrder[],
  inventoryMovements: [] as InventoryMovementItem[],
  inventorySummary: [] as InventorySummaryRow[],
  dispatches: [] as DailyDispatch[],
};

const firstFulfilled = <T,>(result: PromiseSettledResult<T>, fallback: T): T =>
  result.status === 'fulfilled' ? result.value : fallback;

const dueTime = (task: Task) => {
  const raw = task.dueDate ?? task.startDate;
  const time = raw ? new Date(raw).getTime() : Number.NaN;
  return Number.isNaN(time) ? Number.MAX_SAFE_INTEGER : time;
};

export const taskSequenceValue = (task: Task) =>
  typeof task.bqSequence === 'number' && Number.isFinite(task.bqSequence)
    ? task.bqSequence
    : Number.MAX_SAFE_INTEGER;

export const compareTasksByExecutionOrder = (a: Task, b: Task) =>
  taskSequenceValue(a) - taskSequenceValue(b)
  || dueTime(a) - dueTime(b)
  || String(a.locationId ?? '').localeCompare(String(b.locationId ?? ''))
  || String(a.taskCode ?? a.title).localeCompare(String(b.taskCode ?? b.title));

export default function usePmFieldData(): PmFieldData {
  const { activeProjectId, projects } = useSelector((state: RootState) => state.projects);
  const [rows, setRows] = useState(emptyRows);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  const projectName = projects.find((project) => project.id === activeProjectId)?.name ?? 'Active project';

  useEffect(() => {
    if (!activeProjectId) {
      setRows(emptyRows);
      return;
    }

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const [
          tasksResult,
          overviewResult,
          permitsResult,
          inspectionsResult,
          bonusResult,
          accountsTransactionsResult,
          purchaseOrdersResult,
          inventoryMovementsResult,
          inventorySummaryResult,
          dispatchesResult,
        ] = await Promise.allSettled([
          loadAllProjectTasks(activeProjectId),
          teamService.getOverview(activeProjectId),
          permitsService.list({ projectId: activeProjectId }),
          inspectionsService.list({ projectId: activeProjectId }),
          teamService.getBonusPoints({ projectId: activeProjectId }),
          accountsTransactionService.list(activeProjectId),
          procurementService.listPurchaseOrders({ projectId: activeProjectId, limit: 100 }),
          inventoryMovementService.getMovements({ projectId: activeProjectId, limit: 100 }),
          resourceService.getInventorySummary(),
          dailyDispatchService.listDispatches({ projectId: activeProjectId }),
        ]);
        if (cancelled) return;
        if (tasksResult.status === 'rejected') throw tasksResult.reason;

        const tasks = tasksResult.value;
        const resourceTaskMap = new Map<string, Task>();
        tasks
          .filter((task) => isOpenTask(task) && isConstructionExecutionTask(task))
          .sort(compareTasksByExecutionOrder)
          .slice(0, RESOURCE_TASK_CAP)
          .forEach((task) => resourceTaskMap.set(task.id, task));
        tasks
          .filter((task) => isConstructionExecutionTask(task) && Boolean(task.pmIssuedToSupervisorId || task.pmIssuedToSupervisor))
          .forEach((task) => resourceTaskMap.set(task.id, task));
        const resourceTasks = [...resourceTaskMap.values()];
        const resourceResults = await Promise.allSettled(
          resourceTasks.map((task) => taskMaterialsService.listForTask(task.id))
        );
        if (cancelled) return;
        const taskResources: Record<string, TaskMaterial[]> = {};
        resourceTasks.forEach((task, index) => {
          const result = resourceResults[index];
          if (result.status === 'fulfilled' && result.value.length) {
            taskResources[task.id] = result.value;
          }
        });

        setRows({
          tasks,
          taskResources,
          teamOverview: firstFulfilled(overviewResult, []),
          permits: firstFulfilled(permitsResult, []),
          inspections: firstFulfilled(inspectionsResult, []),
          bonusPoints: firstFulfilled(bonusResult, null),
          accountsTransactions: firstFulfilled(accountsTransactionsResult, []),
          purchaseOrders: firstFulfilled(purchaseOrdersResult, { rows: [], total: 0, page: 1, limit: 100, totalPages: 1 }).rows,
          inventoryMovements: firstFulfilled(inventoryMovementsResult, []),
          inventorySummary: firstFulfilled(inventorySummaryResult, []),
          dispatches: firstFulfilled(dispatchesResult, []),
        });
      } catch (err: any) {
        if (!cancelled) setError(err?.response?.data?.message || 'Project management field data could not be loaded.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [activeProjectId, reloadToken]);

  const reload = useCallback(() => setReloadToken((value) => value + 1), []);

  return {
    projectId: activeProjectId,
    projectName,
    ...rows,
    loading,
    error,
    reload,
  };
}
